import type { ChatBatteryState, ChatQuota } from '@nutians/shared/schemas';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

interface QuotaCounterProps {
  quota: ChatQuota | null;
  battery?: ChatBatteryState | null;
  /** When provided, an "Upgrade" link appears once the quota is exhausted. */
  upgradeTo?: string;
  /**
   * When provided, the exhausted-state "Upgrade" control becomes a button that
   * starts a real checkout (ADR-0022 §6) instead of linking to `upgradeTo`.
   */
  onUpgrade?: () => void;
  /** Disables the upgrade button while a checkout request is in flight. */
  upgrading?: boolean;
  /** Awards today's check-in credits and updates the parent battery state. */
  onCheckIn?: () => void;
  checkingIn?: boolean;
  /** Opens the referral share affordance when referral is available. */
  onShare?: () => void;
  sharing?: boolean;
}

function formatEta(seconds: number, t: ReturnType<typeof useTranslation>['t']) {
  if (seconds <= 0) return t('battery.timeReady');
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours <= 0) return t('battery.timeMinutes', { count: minutes });
  if (mins === 0) return t('battery.timeHours', { count: hours });
  return t('battery.timeHoursMinutes', { hours, minutes: mins });
}

function UpgradeControl({
  upgradeTo,
  onUpgrade,
  upgrading,
  label,
}: Pick<QuotaCounterProps, 'upgradeTo' | 'onUpgrade' | 'upgrading'> & { label: string }) {
  const { t } = useTranslation('coach');
  if (onUpgrade) {
    return (
      <button
        type="button"
        onClick={onUpgrade}
        disabled={upgrading}
        className="rounded-full border border-leaf/30 bg-leaf/10 px-3 py-1
                   text-xs font-semibold text-leaf-deep hover:bg-leaf/15
                   focus:outline-none focus:ring-2 focus:ring-leaf
                   disabled:opacity-60"
        aria-label={t('quota.upgradeAria')}
      >
        {upgrading ? t('battery.starting') : label}
      </button>
    );
  }
  if (!upgradeTo) return null;
  return (
    <Link
      to={upgradeTo}
      className="rounded-full border border-leaf/30 bg-leaf/10 px-3 py-1
                 text-xs font-semibold text-leaf-deep hover:bg-leaf/15
                 focus:outline-none focus:ring-2 focus:ring-leaf"
      aria-label={t('quota.upgradeAria')}
    >
      {label}
    </Link>
  );
}

export function QuotaCounter({
  quota,
  battery,
  upgradeTo,
  onUpgrade,
  upgrading,
  onCheckIn,
  checkingIn,
  onShare,
  sharing,
}: QuotaCounterProps) {
  const { t } = useTranslation('coach');

  if (battery) {
    const remaining = battery.regenCredits + battery.earnedCredits;
    const eta = formatEta(battery.regenEtaSeconds, t);

    if (remaining > 0) {
      return (
        <div role="status" className="flex items-center gap-2 text-xs" aria-live="polite">
          <span className="rounded-full bg-husk px-2.5 py-1 font-medium tabular-nums text-ink-soft">
            {t('battery.remaining', { count: remaining, capacity: battery.capacity })}
          </span>
          {battery.regenCredits < battery.capacity && (
            <span className="text-ink-soft">{t('battery.rechargesIn', { time: eta })}</span>
          )}
        </div>
      );
    }

    return (
      <div
        role="status"
        aria-live="polite"
        className="w-full rounded-2xl border border-leaf/20 bg-leaf/10 px-4 py-3 text-sm text-ink"
      >
        <p className="font-medium">{t('battery.emptyTitle')}</p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="rounded-full bg-shell px-3 py-1 text-xs text-ink-soft">
            {t('battery.rechargesIn', { time: eta })}
          </span>
          {battery.canEarnCheckIn && onCheckIn && (
            <button
              type="button"
              onClick={onCheckIn}
              disabled={checkingIn}
              className="rounded-full border border-leaf/30 bg-shell px-3 py-1 text-xs
                         font-semibold text-leaf-deep focus:outline-none focus:ring-2
                         focus:ring-leaf disabled:opacity-60"
            >
              {checkingIn ? t('battery.earning') : t('battery.checkInCta')}
            </button>
          )}
          {onShare && (
            <button
              type="button"
              onClick={onShare}
              disabled={sharing}
              className="rounded-full border border-husk bg-shell px-3 py-1 text-xs
                         font-semibold text-ink focus:outline-none focus:ring-2
                         focus:ring-leaf disabled:opacity-60"
            >
              {sharing ? t('battery.sharing') : t('battery.shareCta')}
            </button>
          )}
          {battery.canUpgrade && (
            <UpgradeControl
              upgradeTo={upgradeTo}
              onUpgrade={onUpgrade}
              upgrading={upgrading}
              label={t('battery.premiumCta')}
            />
          )}
        </div>
      </div>
    );
  }

  if (!quota) return null;

  if (quota.limit === null) {
    return null;
  }

  const remaining = quota.limit - quota.used;
  const resetsAt = new Date(quota.resetsAt);
  const exhausted = remaining <= 0;

  return (
    <div role="status" className="flex items-center gap-2 text-xs" aria-live="polite">
      <span className="sr-only">
        {t('quota.usedAria', { used: quota.used, limit: quota.limit })}
      </span>
      <span
        aria-hidden="true"
        className={`font-medium tabular-nums ${
          exhausted ? 'text-acorn dark:text-acorn' : 'text-ink-soft dark:text-ink-soft'
        }`}
      >
        {t('quota.used', { used: quota.used, limit: quota.limit })}
      </span>
      {exhausted && (
        <span className="text-ink-soft dark:text-ink-soft">
          {t('quota.resets', {
            time: resetsAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
          })}
        </span>
      )}
      {exhausted && (
        <UpgradeControl
          upgradeTo={upgradeTo}
          onUpgrade={onUpgrade}
          upgrading={upgrading}
          label={t('quota.upgrade')}
        />
      )}
    </div>
  );
}
